import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import logoImage from "@assets/logo-e-zez-png-3.png";
import EmailVerification from "@/components/EmailVerification";

declare global {
  interface Window {
    grecaptcha: any;
    onClick: (e: Event) => void;
  }
}

const loginSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function AuthPage() {
  const [, navigate] = useLocation();
  const { user, loginMutation, isLoading } = useAuth();
  const [needsVerification, setNeedsVerification] = useState(false);
  const [userId, setUserId] = useState<number | undefined>(undefined);

  // Effect for redirection - safer than conditional rendering
  useEffect(() => {
    if (user) {
      navigate("/admin");
    }
  }, [user, navigate]);

  // Load Google reCAPTCHA v3
  useEffect(() => {
    const loadRecaptcha = () => {
      // Check if script is already loaded
      if (window.grecaptcha) {
        return Promise.resolve();
      }

      const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
      if (existingScript) {
        return new Promise((resolve) => {
          const checkLoaded = () => {
            if (window.grecaptcha) {
              resolve(undefined);
            } else {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=6Lco9FMrAAAAAN55BhPO55P2tQfBYxVgQyqjWlx0';
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          // Wait for grecaptcha to be available
          const checkGrecaptcha = () => {
            if (window.grecaptcha && window.grecaptcha.ready) {
              resolve(undefined);
            } else {
              setTimeout(checkGrecaptcha, 100);
            }
          };
          checkGrecaptcha();
        };
        
        script.onerror = () => {
          console.error('Failed to load reCAPTCHA script');
          reject(new Error('reCAPTCHA script failed to load'));
        };
        
        document.head.appendChild(script);
      });
    };

    loadRecaptcha().catch((error) => {
      console.error('reCAPTCHA loading error:', error);
    });
  }, []);

  // Login form
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onLoginSubmit = async (values: LoginFormValues) => {
    const executeWithCaptcha = async () => {
      if (!window.grecaptcha || !window.grecaptcha.ready || !window.grecaptcha.execute) {
        console.warn('reCAPTCHA not available, proceeding without it');
        return loginMutation.mutate(values);
      }

      try {
        await new Promise<void>((resolve) => {
          window.grecaptcha.ready(() => resolve());
        });

        const token = await window.grecaptcha.execute('6Lco9FMrAAAAAN55BhPO55P2tQfBYxVgQyqjWlx0', { action: 'login' });
        
        if (!token) {
          console.warn('reCAPTCHA token not generated, proceeding without it');
          return loginMutation.mutate(values);
        }

        // Add token to the form data
        const formDataWithToken = { ...values, captcha: token };
        
        loginMutation.mutate(formDataWithToken, {
          onSuccess: (data) => {
            if (data.success) {
              // Normal login success is handled by the mutation
            } else if (data.needsVerification) {
              // Show verification UI
              setNeedsVerification(true);
              setUserId(data.userId);
            }
          },
          onError: () => {
            console.error('Login failed');
          }
        });
      } catch (error) {
        console.error('reCAPTCHA execution error:', error);
        // Still allow form submission if reCAPTCHA fails
        loginMutation.mutate(values);
      }
    };

    await executeWithCaptcha();
  };

  

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Auth Header */}
      <div className="bg-black text-white py-4 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          {/* Logo and Back Button Row */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center">
              <div className="flex items-center gap-3">
                <img src={logoImage} alt="Swiss Welding" className="h-12 w-auto" />
                <h1 className="text-2xl md:text-3xl font-bold">Admin Login</h1>
              </div>
            </div>
          </div>
          
          {/* Navigation Links Row */}
          <div className="hidden md:flex justify-center space-x-6 mt-2">
            <button 
              onClick={() => navigate('/')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/#about')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ABOUT
            </button>
            <button 
              onClick={() => navigate('/#services')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              SERVICES
            </button>
            <button 
              onClick={() => navigate('/gallery')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              GALLERY
            </button>
            <button 
              onClick={() => navigate('/#testimonials')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              TESTIMONIALS
            </button>
            <button 
              onClick={() => navigate('/#timeline')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              TIMELINE
            </button>
            <button 
              onClick={() => navigate('/#estimation')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              ESTIMATE
            </button>
            <button 
              onClick={() => navigate('/#booking')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              BOOK NOW
            </button>
            <button 
              onClick={() => navigate('/#contact')} 
              className="text-white hover:text-red-400 transition-colors font-medium"
            >
              CONTACT
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex-grow flex items-center justify-center py-12">
        <div className="w-full max-w-4xl flex flex-col md:flex-row">
          {/* Left Column - Form or Verification */}
          <Card className="w-full md:w-1/2 p-6 shadow-lg">
            {needsVerification ? (
              <EmailVerification 
                userId={userId} 
                onCancel={() => setNeedsVerification(false)} 
              />
            ) : (
              <div className="w-full">
                <div className="flex flex-col items-center mb-6">
                  <h3 className="text-xl font-semibold mb-1">Administrator Login</h3>
                  <p className="text-sm text-gray-500 text-center">
                    This area is restricted to authorized administrators only.
                  </p>
                </div>
                
                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Username</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter your username" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="Enter your password" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="text-center text-sm text-gray-500 mb-2">
                      <p>This site is protected by reCAPTCHA and the Google</p>
                      <p>
                        <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                        <a href="https://policies.google.com/terms" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                      </p>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-black text-white hover:bg-primary"
                      disabled={loginMutation.isPending}
                    >
                      {loginMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Login
                    </Button>
                  </form>
                </Form>
                
                <div className="mt-6 text-center">
                  <Button 
                    variant="link" 
                    onClick={() => navigate('/')}
                    className="text-gray-500 hover:text-black"
                  >
                    Return to Homepage
                  </Button>
                </div>
              </div>
            )}
          </Card>
          
          {/* Right Column - Hero */}
          <div className="hidden md:flex md:w-1/2 p-8 bg-black text-white flex-col justify-center">
            <h2 className="text-3xl font-bold mb-4">Swiss Welding Admin</h2>
            <p className="mb-6">
              Welcome to the admin area. This secure area allows administrators to manage the website content.
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
                Upload and manage gallery images
              </li>
              <li className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
                Categorize images for easy browsing
              </li>
              <li className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
                Create and manage website content
              </li>
            </ul>
            <p className="text-sm text-gray-400">
              If you need an account, please contact the system administrator.
            </p>
          </div>
        </div>
      </div>
      
      {/* Floating Home Button */}
      <div className="fixed bottom-8 right-8 z-10">
        <Button
          onClick={() => navigate('/')}
          className="bg-black text-white hover:bg-primary shadow-xl rounded-full p-4 h-14 w-14 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </Button>
      </div>
      
      {/* Footer */}
      <div className="bg-black text-white py-6 text-center mt-auto">
        <p>© {new Date().getFullYear()} Swiss Welding LLC. All rights reserved.</p>
      </div>
    </div>
  );
}
